import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { PrintNodeService } from '../print-node.service'
import { UserTenantSettings } from '../../users/models/user.entity'
import { Computer, Printer } from '../models/print-node.entity'
import { lastValueFrom } from 'rxjs'

interface PrinterDetailState {
  computerId?: string
  printerId?: string
  computers: Computer[]
  printers: Printer[]
  qtyToPrint: number
  type: string
  isLoading: boolean
  settings?: UserTenantSettings
  error?: HttpErrorResponse
}

const PRINTER_DETAIL_INITIAL_STATE: PrinterDetailState = {
  computers: [],
  printers: [],
  qtyToPrint: 1,
  type: 'label',
  isLoading: false,
}

@Injectable()
export class PrinterDetailStore {
  constructor(private printService: PrintNodeService) {}

  private state = signal<PrinterDetailState>(PRINTER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly computerId = computed(() => this.state().computerId)
  readonly printerId = computed(() => this.state().printerId)
  readonly computers = computed(() => this.state().computers)
  readonly printers = computed(() => this.state().printers)
  readonly type = computed(() => this.state().type)
  readonly settings = computed(() => this.state().settings)
  readonly qtyToPrint = computed(() => this.state().qtyToPrint)
  readonly isLoading = computed(() => this.state().isLoading)

  // Effects

  async load(
    type: string,
    settings?: UserTenantSettings,
    computerId?: string,
  ): Promise<void> {
    if (!computerId) {
      const computers = await lastValueFrom(this.printService.listComputers$())
      this.setComputers(computers)
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.printService.listComputerPrinters$(computerId).subscribe({
        next: async (data) => {
          resolve()
          const computers = await lastValueFrom(
            this.printService.listComputers$(),
          )
          this.setData(data, type, computers, computerId, settings)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(
    printers: Printer[],
    type: string,
    computers: Computer[],
    computerId?: string,
    settings?: UserTenantSettings,
  ) {
    const cpuId = computerId ?? settings?.computerId ?? this.computerId()
    const backupPrinter: Printer | undefined = this.printers.length
      ? printers[0]
      : undefined
    const printerId =
      type === 'label'
        ? (settings?.printers?.label ?? backupPrinter?.id)
        : type === 'summary'
          ? (settings?.printers?.a4 ?? backupPrinter?.id)
          : undefined

    this.state.set({
      ...this.state(),
      printers,
      computers,
      type,
      settings,
      computerId: cpuId,
      printerId,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setSettings(settings: UserTenantSettings) {
    this.state.set({ ...this.state(), settings })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setPrinterId(printerId?: string) {
    this.state.set({ ...this.state(), isLoading: false, printerId })
  }

  setQtyToPrint(qtyToPrint: number) {
    this.state.set({ ...this.state(), isLoading: false, qtyToPrint })
  }

  setComputers(computers: Computer[]) {
    this.state.set({ ...this.state(), isLoading: false, computers })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
